import { Icon, DefaultButton, TooltipHost } from "@fluentui/react";
import { useInjectedService } from "../../services/ioc/service-dependency";

import "./bkchome.css";
import { useParams } from "react-router-dom";
import { getUserLang, isNullOrEmpty } from "../../lib/helper";
import { Fragment, useState } from "react";

const BkcHome = (props: { pathname: string }) => {
  const { localizationService } = useInjectedService();
  let { culture } = useParams();

  if (isNullOrEmpty(culture) || culture === undefined) {
    culture = getUserLang(props.pathname);
  }

  var pdfCulture = "en";
  if (culture !== undefined && localizationService.isValidCulture(culture, props.pathname)) {
    pdfCulture = culture;
  }

  const [completedSteps, setCompletedSteps] = useState<
    Array<{ step: string; question: string; answer: string; action: string }>
  >([]);
  const [currentStepKey, setCurrentStepKey] = useState<string>("start");

  // Define types for wizard
  interface WizardOption {
    text: string;
    nextStep: string;
    action: string;
    highlight?: boolean;
    className?: string;
  }

  interface WizardStep {
    question: string;
    questionInfo?: string;
    options: WizardOption[];
    optionsClassName?: string;
    question2?: string;
    options2?: WizardOption[];
    options2ClassName?: string;
    questionInfo2?: string;
    question3?: string;
    options3?: WizardOption[];
    options3ClassName?: string;
    questionInfo3?: string;
  }

  const dontShowCleanRoomStep = function (): boolean {
    return pdfCulture === "es";
  };

  // Wizard step definitions
  const wizardSteps: Record<string, WizardStep> = {
    start: {
      question: localizationService.getLocalizedText("WizardKnowCredentials"),
      options: [
        {
          text: localizationService.getLocalizedText("WizardYes"),
          nextStep: "authenticator",
          action: "knowsCredentials",
        },
        {
          text: localizationService.getLocalizedText("WizardNo"),
          nextStep: "contact",
          action: "needsCredentials",
        },
      ],
    },
    authenticator: {
      question: localizationService.getLocalizedText("WizardHasAuthenticator"),
      questionInfo: localizationService.getLocalizedText(
        "WizardHasAuthenticatorInfo"
      ),
      options: [
        {
          text: localizationService.getLocalizedText("WizardYes"),
          nextStep: "device",
          action: "hasAuthenticator",
        },
        {
          text: localizationService.getLocalizedText("WizardCannotAccess"),
          nextStep: "reset",
          action: "cannotAccess",
        },
        {
          text: localizationService.getLocalizedText("WizardNo"),
          nextStep: "cleanroom",
          action: "needsSetup",
        },
      ],
    },
    device: {
      question: localizationService.getLocalizedText("WizardDeviceType"),
      options: [
        {
          text: localizationService.getLocalizedText("WizardIOS"),
          nextStep: "final_guide_ios",
          action: "selectiOS",
        },
        {
          text: localizationService.getLocalizedText("WizardAndroid"),
          nextStep: "final_guide_android",
          action: "selectAndroid",
        },
      ],
    },
    cleanroom: {
      question: "",
      options: [
        {
          text: localizationService.getLocalizedText(
            "WizardDownloadAppAuthenticatorAndroid"
          ),
          nextStep: "",
          action: "downloadMicrosoftAuthenticatorAndroid",
          highlight: true,
        },
        {
          text: localizationService.getLocalizedText(
            "WizardDownloadAppAuthenticatorIos"
          ),
          nextStep: "",
          action: "downloadMicrosoftAuthenticatorIos",
          highlight: true,
        },
      ],
      question2: dontShowCleanRoomStep() ? "" : localizationService.getLocalizedText("WizardCleanroom"),
      options2: dontShowCleanRoomStep() ? [
        {
          text: localizationService.getLocalizedText("WizardOpenGuideSmartphoneAndLaptop"),
          nextStep: "",
          action: "openGuide",
          highlight: true,
        }
      ] : [
        {
          text: localizationService.getLocalizedText("WizardYes"),
          nextStep: "cleanroom_guide",
          action: "inCleanroom",
        },
        {
          text: localizationService.getLocalizedText("WizardNo"),
          nextStep: "cleanroom_guide_no",
          action: "notInCleanroom",
        },
      ],
      question3: dontShowCleanRoomStep() ? localizationService.getLocalizedText("WizardDeviceType") : undefined,
      options3: dontShowCleanRoomStep() ? [
        {
          text: localizationService.getLocalizedText("WizardIOS"),
          nextStep: "final_guide_ios",
          action: "selectiOS",
        },
        {
          text: localizationService.getLocalizedText("WizardAndroid"),
          nextStep: "final_guide_android",
          action: "selectAndroid",
        },
      ] : undefined,
    },
    contact: {
      question: localizationService.getLocalizedText("WizardContactDVK"),
      options: [],
    },
    reset: {
      question: localizationService.getLocalizedText("WizardResetAuth"),
      options: [],
    },
    setup: {
      question: localizationService.getLocalizedText("WizardMFARequired"),
      options: [
        {
          text: localizationService.getLocalizedText("WizardSetupMFA"),
          nextStep: "mfa_guide",
          action: "setupMFA",
          highlight: true,
        },
      ],
    },
    mfa_guide: {
      question: "",
      options: [
        {
          text: localizationService.getLocalizedText(
            "WizardOpenGuideSmartphone"
          ),
          nextStep: "device",
          action: "openGuideSmartphoneOnly",
          highlight: true,
          className: "wizard-option-open-smartphone",
        },
      ],
      question2: localizationService.getLocalizedText(
        "WizardOpenGuideSmartphoneAndLaptopInfo"
      ),
      options2: [
        {
          text: localizationService.getLocalizedText(
            "WizardOpenGuideSmartphoneAndLaptop"
          ),
          nextStep: "device",
          action: "openGuide",
          highlight: true,
        },
      ],
    },
    cleanroom_guide: {
      question: localizationService.getLocalizedText(
        "WizardCleanroomGuideInsturctions"
      ),
      options: [
        {
          text: localizationService.getLocalizedText("WizardOpenGuideAndroid"),
          nextStep: "device",
          action: "openGuideCleanroomAndroid",
          highlight: true,
        },
        {
          text: localizationService.getLocalizedText("WizardOpenGuideIOS"),
          nextStep: "device",
          action: "openGuideCleanroomIOS",
          highlight: true,
        },
      ],
    },
     cleanroom_guide_no: {
      question: "",
      options: [
        {
          text: localizationService.getLocalizedText("WizardOpenGuideSmartphone"),
          nextStep: "",
          action: "openGuideSmartphoneOnly",
          highlight: true
        },
        {
          text: localizationService.getLocalizedText("WizardOpenGuideSmartphoneAndLaptop"),
          nextStep: "",
          action: "openGuide",
          highlight: true,
        }
      ],
      question2: localizationService.getLocalizedText("WizardDeviceType"),
      options2: [
        {
          text: localizationService.getLocalizedText("WizardIOS"),
          nextStep: "final_guide_ios",
          action: "selectiOS",
        },
        {
          text: localizationService.getLocalizedText("WizardAndroid"),
          nextStep: "final_guide_android",
          action: "selectAndroid",
        },
      ],
    },
    final_guide_ios: {
      question: localizationService.getLocalizedText(
        "WizardFinalInstructionsIos"
      ),
      options: [
        {
          text: localizationService.getLocalizedText("WizardDownloadApp"),
          nextStep: "",
          action: "downloadAppIos",
          highlight: true,
        },
      ],
      optionsClassName: "wizard-options-qr-code-container",
    },
    final_guide_android: {
      question: localizationService.getLocalizedText("WizardAndroidStep1Info"),
      options: [
        {
          text: localizationService.getLocalizedText("WizardAndroidStep1"),
          nextStep: "",
          action: "downloadCompanyPortal",
          highlight: true,
        },
      ],
      question2: localizationService.getLocalizedText("WizardAndroidStep2"),
      options2: [
        {
          text: localizationService.getLocalizedText("WizardDownloadApp"),
          nextStep: "",
          action: "downloadAppAndroid",
          highlight: true,
        },
      ],
      options2ClassName: "wizard-options-qr-code-container",
    },
  };

  const handleWizardAction = (
    action: string,
    nextStep: string,
    optionText: string
  ) => {
    const currentStep = wizardSteps[currentStepKey];
    if (currentStep) {
      // Add the completed step to the history
      setCompletedSteps((prev) => [
        ...prev,
        {
          step: currentStepKey,
          question: !isNullOrEmpty(currentStep.question)
            ? currentStep.question
            : currentStep.question2 ?? "",
          answer: optionText,
          action: action,
        },
      ]);
    }

    if (nextStep === "start" || action === "understood") {
      // End of wizard path or restart
      setCurrentStepKey("completed");
    } else {
      setCurrentStepKey(nextStep);
    }

    setTimeout(() => {
      if (document.getElementsByClassName("wizard-current-step").length > 0) {
        document
          .getElementsByClassName("wizard-current-step")[0]
          .scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }, 50);
  };

  const goBack = () => {
    if (completedSteps.length === 0) return;
    const prev = completedSteps[completedSteps.length - 1];
    setCompletedSteps((prevSteps) => prevSteps.slice(0, -1));
    setCurrentStepKey(prev.step);

    setTimeout(() => {
      if (document.getElementsByClassName("wizard-current-step").length > 0) {
        document
          .getElementsByClassName("wizard-current-step")[0]
          .scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }, 50);
  };

  const jumpToStep = (stepKey: string) => {
    // Remove all completed steps after the clicked step
    const clickedStepIndex = completedSteps.findIndex(
      (step) => step.step === stepKey
    );
    if (clickedStepIndex !== -1) {
      setCompletedSteps((prev) => prev.slice(0, clickedStepIndex));
      setCurrentStepKey(stepKey);
    }

    setTimeout(() => {
      if (document.getElementsByClassName("wizard-current-step").length > 0) {
        document
          .getElementsByClassName("wizard-current-step")[0]
          .scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }, 50);
  };

  const renderCompletedSteps = () => {
    return completedSteps.map((step, index) => (
      <div
        key={index}
        className="wizard-completed-step clickable"
        onClick={() => jumpToStep(step.step)}
        title="Klicken, um zu dieser Frage zurückzukehren"
      >
        <div
          className="wizard-completed-question"
          dangerouslySetInnerHTML={{ __html: step.question }}
        />
        <div className="wizard-completed-answer">✓ {step.answer}</div>
      </div>
    ));
  };

  const renderCurrentStep = () => {
    const step = wizardSteps[currentStepKey];
    if (!step) return null;

    const getDownloadUrl = (action: string) => {
      switch (action) {
        case "openGuideLaptopOnly":
          return require("../../assets/files/Manual_MFA_Laptop_only_" +
            pdfCulture +
            ".pdf");
        case "openGuide":
          return require("../../assets/files/Manual_MFA_Laptop_and_Smartphone_" +
            pdfCulture +
            ".pdf");
        case "openGuideSmartphoneOnly":
          return require("../../assets/files/Manual_MFA_Smartphone_only_" +
            pdfCulture +
            ".pdf");
        case "openGuideCleanroomAndroid":
          return require("../../assets/files/Android_2025_" +
            pdfCulture +
            ".pdf");
        case "openGuideCleanroomIOS":
          return require("../../assets/files/iOS_2025_" + pdfCulture + ".pdf");
        case "downloadAppIos":
          return "https://apps.apple.com/de/app/bkc-app/id1597272008";
        case "downloadAppAndroid":
          return "https://play.google.com/store/apps/details?id=com.bbraun.bkc";
        case "downloadCompanyPortal":
          return "https://play.google.com/store/apps/details?id=com.microsoft.windowsintune.companyportal";
        case "downloadMicrosoftAuthenticatorAndroid":
          return "https://play.google.com/store/apps/details?id=com.azure.authenticator";
        case "downloadMicrosoftAuthenticatorIos":
          return "https://apps.apple.com/de/app/microsoft-authenticator/id983156458";
        default:
          return null;
      }
    };

    const getQRCode = (action: string) => {
      switch (action) {
        case "downloadAppIos":
          return require("../../assets/images/QRCodeAppStore.png");
        case "downloadAppAndroid":
          return require("../../assets/images/QRCodePlaystore.png");
        case "downloadCompanyPortal":
          return require("../../assets/images/QRCodeCompanyPortal.png");
        case "downloadMicrosoftAuthenticatorAndroid":
          return require("../../assets/images/QRCodeMicrosoftAuthenticatorAndroid.png");
        case "downloadMicrosoftAuthenticatorIos":
          return require("../../assets/images/QRCodeMicrosoftAuthenticatorIOS.png");
        default:
          return null;
      }
    };

    const renderQuestionAndOptions = (
      question: string,
      options: WizardOption[],
      isSecondary = false,
      questionInfo?: string,
      optionsClassName?: string
    ) => {
      return (
        <div
          className={`wizard-section ${
            isSecondary ? "wizard-section-secondary" : ""
          }`}
        >
          {question && question.length > 0 && (
            <div className="wizard-question">
              <span dangerouslySetInnerHTML={{ __html: question }} />
              {questionInfo && (
                <TooltipHost
                  content={questionInfo}
                  id={"wizard-question-tooltip"}
                  calloutProps={{ gapSpace: 0 }}
                  styles={{ root: { display: "inline-block" } }}
                >
                  <Icon
                    iconName="Info"
                    className="wizard-question-info-icon"
                    aria-describedby="wizard-question-tooltip"
                  />
                </TooltipHost>
              )}
            </div>
          )}
          {options.length > 0 && (
            <div
              className={`wizard-options ${
                optionsClassName ? optionsClassName : ""
              }`}
            >
              {options.map((option, index) => {
                const downloadUrl = getDownloadUrl(option.action);
                const qrCode = getQRCode(option.action);

                if (downloadUrl) {
                  return (
                    <Fragment key={index}>
                      {qrCode && (
                        <div className="wizard-options-qr-code-container">
                          <img
                            alt="BBraun_MFA_Logo"
                            src={qrCode}
                            data-midwidth="800"
                            data-maxwidth="0"
                          />
                        </div>
                      )}
                      <a
                        key={index}
                        href={downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`wizard-button wizard-link ${
                          option.highlight ? "wizard-button-highlight" : ""
                        } ${option.className ? option.className : ""}`}
                        onClick={() => {
                          setTimeout(() => {
                            option.nextStep &&
                              handleWizardAction(
                                option.action,
                                option.nextStep,
                                option.text
                              );
                          }, 500);
                          return true;
                        }}
                      >
                        {option.text}
                      </a>
                    </Fragment>
                  );
                }

                return (
                  <DefaultButton
                    key={index}
                    className={`wizard-button ${
                      option.highlight ? "wizard-button-highlight" : ""
                    } ${option.className ? option.className : ""}`}
                    onClick={() =>
                      handleWizardAction(
                        option.action,
                        option.nextStep,
                        option.text
                      )
                    }
                    text={option.text}
                  />
                );
              })}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="wizard-container wizard-current-step bbraun-sand-bg">
        {completedSteps.length > 0 && (
          <div className="wizard-back-button-container">
            <DefaultButton
              iconProps={{ iconName: "Up" }}
              text={localizationService.getLocalizedText("WizardBack")}
              aria-label="Back"
              onClick={goBack}
              className="wizard-back-button"
            />
          </div>
        )}

        {/* Render primary question and options */}
        {renderQuestionAndOptions(
          step.question,
          step.options,
          false,
          step.questionInfo,
          step.optionsClassName
        )}

        {/* Render secondary question and options if they exist */}
        {step.options2 &&
          renderQuestionAndOptions(
            step.question2 || "",
            step.options2,
            true,
            step.questionInfo2,
            step.options2ClassName
          )}

        {/* Render tertiary question and options if they exist */}
        {step.options3 &&
          renderQuestionAndOptions(
            step.question3 || "",
            step.options3,
            true,
            step.questionInfo3,
            step.options3ClassName
          )}
      </div>
    );
  };

  return (
    <div>
      <div className="row">
        <div className="col-sm">
          <div className="bbraun-hero">
            <div className="bbraun-hero-subtitle">
              {localizationService.getLocalizedText("WizardWelcomeHome")}
            </div>
            <div className="bbraun-hero-headline">
              {localizationService.getLocalizedText("WizardBKCAppTitle")}
            </div>
            <div
              className="bbraun-hero-content"
              dangerouslySetInnerHTML={{
                __html:
                  localizationService.getLocalizedText("WizardBKCAppDesc"),
              }}
            />
          </div>
        </div>
      </div>

      {/* Interactive Wizard - Always Visible */}
      <div className="row wizard-section bbraun-sand-bg">
        <div className="col-sm">
          <div className="wizard-content">
            {/* Render completed steps */}
            {renderCompletedSteps()}

            {/* Render current step */}
            {renderCurrentStep()}
          </div>
        </div>
      </div>
      <div className="row bbraun-green-bg bbraun-help-container">
        <div className="col-sm">
          <div className="bbraun-hero">
            <div className="bbraun-hero-headline">
              <Icon className="bbraun-help-icon" iconName="Mail" />
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    localizationService.getLocalizedText("WizardNeedHelp"),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BkcHome;
